import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {ActivatedRoute, Router} from "@angular/router";
import {TourService} from "../classes/tour.service";
import {Tour, TourModel} from "../classes/tour.model";
import {listAll, ref, Storage, ListResult, getDownloadURL, uploadBytes} from "@angular/fire/storage";
import {MatIconModule} from "@angular/material/icon";
import {MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule} from "@angular/material/tree";
import {FlatTreeControl} from "@angular/cdk/tree";
import {BehaviorSubject, debounceTime, distinctUntilChanged, filter} from "rxjs";
import {CommonModule} from "@angular/common";
import {MatTooltipModule} from "@angular/material/tooltip";
import {LanguageService} from "../classes/language.service";
import {Functions, getFunctions, httpsCallable} from "@angular/fire/functions";
import {
  CropperPosition,
  ImageCroppedEvent,
  ImageCropperComponent,
  ImageCropperModule,
  LoadedImage
} from "ngx-image-cropper";
import {DomSanitizer} from "@angular/platform-browser";
import {MaterialFileInputModule} from "ngx-custom-material-file-input";

interface FileNode {
  level: number;
  name: string;
  children?: FileNode[];
  expandable?: boolean;
  downloadUrl?: string;
  step?: string;
}

@Component({
  selector: 'app-tour-creator',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTreeModule,
    MatTooltipModule,
    ImageCropperModule,
    MaterialFileInputModule
  ],
  templateUrl: './tour-creator.component.html',
  styleUrl: './tour-creator.component.scss'
})
export class TourCreatorComponent implements OnInit {
  title: string = 'Add tour';
  type: 'edit' | 'add' = 'add';

  audioHint: boolean = false;

  @ViewChild('audioElement', {static: true}) audioElement: ElementRef;

  @ViewChild('cropperComponent', {static: true}) cropperComponent: ImageCropperComponent;

  protected initCropper: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  form: FormGroup;
  availableLangs: string[] = [];

  private _transformer = (node: FileNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      downloadUrl: node.downloadUrl,
      step: node?.step,
    };
  };
  treeControl = new FlatTreeControl<FileNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children,
  );
  dataSourceUnused = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  dataSourceUsed = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  tourId: string = null;

  files: any[] = [];
  protected tour: Tour;

  //cropper
  imageChangedEvent: any = '';
  croppedImage: any = '';
  showCropper: boolean = false;
  private croppedImageBlob: Blob;
  private rawImageBlob: Blob;
  private cropperData: CropperPosition;

  constructor(
    private tourService: TourService,
    private route: ActivatedRoute,
    private router: Router,
    private storage: Storage,
    protected languageService: LanguageService,
    private functions: Functions,
    private sanitizer: DomSanitizer
  ) {
    this.availableLangs = languageService.availableLangs;

    const index = this.availableLangs.indexOf(languageService.currentLang$.value);

    // If the language is found and it's not already the first element
    if (index > 0) {
      // Remove the user's language from the array
      this.availableLangs.splice(index, 1);

      // Add the user's language to the beginning of the array
      this.availableLangs.unshift(languageService.currentLang$.value);
    }

    const titleFormGroups = this.availableLangs.map((lang) => {
      return [lang, new FormControl('', Validators.required)];
    });

    const descriptionFormGroups = this.availableLangs.map((lang) => {
      return [lang, new FormControl('')];
    });

    this.form = new FormGroup({
      step: new FormControl('', Validators.required, this.stepValidator.bind(this)),
      audioStorageFolder: new FormControl(''),
      image: new FormControl(''),
      title: new FormGroup(Object.fromEntries(titleFormGroups)),
      description: new FormGroup(Object.fromEntries(descriptionFormGroups)),
    });


    route.params.subscribe(params => {
      if (params['id']) {
        console.log('Edit tour', params['id']);
        this.title = 'Edit tour';
        this.tourId = params['id'];
        this.tourService.loadTour(params['id']).then(tour => {
          this.tour = tour;
          this.form.patchValue(tour);

          if (tour.hasImage) {
            this.showCropper = true;
            console.log('Tour has image', tour.imageUrl);
          }

          console.log('Loaded tour', tour);

          if (tour?.audioUrl) {
            this.playSound({downloadUrl: tour.audioUrl}, false);
          }
        });
      }
    });

    this.loadFiles();
  }

  ngOnInit(): void {
      this.initCropper.pipe(debounceTime(100), distinctUntilChanged(), filter(value => value === true)).subscribe((value) => {
        console.log('Cropper ready from init', this.cropperComponent);
        if (this.tour?.cropperData) {
          console.log('Set cropper data', this.tour.cropperData);
          this.cropperComponent.resetCropperPosition();
          this.cropperComponent.cropper = this.tour.cropperData;
          this.cropperComponent.crop();
        }
      });
  }

  private async stepValidator(control: FormControl): Promise<void> {
    if (this.tour?.step === control.value) {
      this.form.get('step').setErrors(null);
      return;
    }

    const tour = await this.tourService.getTourByStepNumber(control.value).catch(() => null);
    if (tour) {
      setTimeout(() => {
        this.form.get('step').setErrors({stepExists: true});
      });
    } else {
      this.form.get('step').setErrors(null);
    }
  }

  async onSubmit() {
    console.log("Form", this.form.value);

    const tour = await Tour.fromJSON(this.form.value);
    let tourId = this.tourId;

    let imgChanged = false;
    if (this.croppedImage) {
      tour.hasImage = true;
      if (tour.cropperData !== this.cropperData) {
        imgChanged = true;
        tour.cropperData = this.cropperData;
      }

    } else {
      tour.hasImage = false;
    }

    console.log('Has image?', tour.hasImage);

    if (this.tourId === null) {
      const addTourResult = await this.tourService.addTour(tour.toJSON());
      tourId = addTourResult.id;
    } else {
      await this.tourService.updateTour({...tour.toJSON(), id: this.tourId});
    }

    if (tour.hasImage && imgChanged) {
      console.log('Cropped image', this.croppedImage);

      if (this.rawImageBlob && this.rawImageBlob.size > 10) {
        const fileRefRaw = ref(this.storage, `tour-images/raw/${tourId}.jpg`);
        const imgResultRaw = await uploadBytes(fileRefRaw, this.rawImageBlob);
      }

      const fileRefCropper = ref(this.storage, `tour-images/cropped/${tourId}.jpg`);

      const imgResult = await uploadBytes(fileRefCropper, this.croppedImageBlob, {contentType: 'image/jpeg', cacheControl: 'public, max-age=31536000'});
      console.log('Image result', imgResult);
    }

    this.router.navigate(['/tours']);
  }

  async loadFiles() {
    let fileData: { name: string, children: any[] }[] = null;
    const cachedData = localStorage.getItem('files');
    if (cachedData) {
      fileData = JSON.parse(cachedData);
      console.log('Files from cache', fileData);
    } else {
      let fileRef = ref(this.storage, 'soundfiles')
      const files = await listAll(fileRef);
      let data = [];

      for (const folder of files.prefixes) {
        const subfolderContents = await listAll(folder);

        data.push({
          name: folder.name, children: subfolderContents.items.map((item) => {
            return {name: item.name, downloadUrl: item.fullPath};
          }), level: 0, expandable: true
        });
      }

      localStorage.setItem('files', JSON.stringify(data));
      console.log('Files without cache', data);

      fileData = data;
    }

    this.files = fileData;
    const usedFiles = [];
    const unusedFiles = [];

    for (const folder of fileData) {
      const tour = await this.tourService.getTourByAudioFolder(folder.name).catch(() => null);
      if (tour) {
        usedFiles.push({...folder, step: tour.step});
      } else {
        unusedFiles.push(folder);
      }
    }

    this.dataSourceUnused.data = await this.convertListToTree(unusedFiles);
    this.dataSourceUsed.data = await this.convertListToTree(usedFiles);
  }

  async convertListToTree(files: { name: string, children: any[], step?: string }[]): Promise<FileNode[]> {
    const rootNode: FileNode[] = [];

    for (const folder of files) {
      const node: FileNode = {name: folder.name, children: [], level: 0, expandable: true, step: folder.step};

      for (const file of folder.children) {
        node.children.push({name: file.name, level: 1, downloadUrl: file.downloadUrl});
      }

      rootNode.push(node);
    }

    return rootNode;
  }

  hasChild = (_: number, node: FileNode) => node.expandable;

  async playSound(node, autoPlay: boolean = true) {
    console.log('Play sound', node);

    const fileRef = ref(this.storage, node.downloadUrl);
    // const audio = new Audio();

    console.log('Audio element', this.audioElement);
    this.audioElement.nativeElement.src = await getDownloadURL(fileRef);

    if (autoPlay) {
      this.audioElement.nativeElement.play();
    }
    // this.audioElement.l();
    // audio.play();
  }

  selectFolder(node) {
    console.log('Select folder', node);
    this.form.get('audioStorageFolder').setValue(node.name);
  }

  async translateTitle(lang: string) {
    const translatedLangs = this.availableLangs.filter(availableLang => availableLang !== lang);
    const urlTranslate = httpsCallable(this.functions, 'translateTour');

    const result = await urlTranslate({
      text: this.form.get(`title.${lang}`).value,
      lang: lang,
      targetLangs: translatedLangs
    });
    console.log('Result', result);

    for (const targetLang of translatedLangs) {
      this.form.get(`title.${targetLang}`).setValue(result.data[targetLang]);
    }
  }

  async translateDescription(lang: string) {
    const translatedLangs = this.availableLangs.filter(availableLang => availableLang !== lang);
    const urlTranslate = httpsCallable(this.functions, 'translateTour');
    const result = await urlTranslate({
      text: this.form.get(`description.${lang}`).value,
      lang: lang,
      targetLangs: translatedLangs
    });

    for (const targetLang of translatedLangs) {
      this.form.get(`description.${targetLang}`).setValue(result.data[targetLang]);
    }
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
    this.rawImageBlob = event.target.files[0];
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(event.objectUrl);

    this.croppedImageBlob = event.blob;
    this.cropperData = event.cropperPosition;
    console.log('Cropped image', this.croppedImage);
    console.log('Data', event.cropperPosition);
    console.log('Data', event.imagePosition);
    // event.blob can be used to upload the cropped image
  }

  imageLoaded(image: LoadedImage) {
    console.log('Image loaded', image);
  }

  cropperReady(cropperData) {
    console.log('Cropper ready', cropperData);
    this.initCropper.next(true);
    // const cropper = this.cropperComponent.nativeElement as ImageCropperComponent;
    // if(this.tour?.cropperData) {
    //   console.log('Cropper', cropper);
    //   console.log('Set cropper data', this.tour.cropperData);
    //   cropper.cropper = this.tour.cropperData;
    // }
    // cropper ready
  }

  loadImageFailed() {
    // show message
  }
}
